import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { AppButton } from '../../../../../shared/components/app-button/app-button';
import { SettingsService } from '../../../../../core/services/settings.service';
import { toast } from 'ngx-sonner';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-dtp-procedure-settings-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DialogModule, CheckboxModule, AppButton],
  template: `
    <p-dialog 
      [visible]="true" 
      [modal]="true" 
      [draggable]="false" 
      [resizable]="false"
      styleClass="!rounded-[2rem] overflow-hidden shadow-2xl"
      (onHide)="onClose()"
      [style]="{ width: '400px' }"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <i class="pi pi-cog"></i>
          </div>
          <div>
            <h3 class="text-lg font-bold text-slate-800">{{ 'procedure_settings' | translate }}</h3>
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{{ procedure?.description }}</p>
          </div>
        </div>
      </ng-template>

      <div class="flex flex-col gap-6 py-4">
        <div class="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-bold text-slate-700">{{ 'is_default' | translate }}</span>
            <span class="text-[10px] text-slate-400 font-medium italic">{{ 'is_default_desc' | translate }}</span>
          </div>
          <p-checkbox [(ngModel)]="settings.isDefault" [binary]="true"></p-checkbox>
        </div>

        <div class="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-bold text-slate-700">{{ 'new_appointment_suggestion' | translate }}</span>
            <span class="text-[10px] text-slate-400 font-medium italic">{{ 'suggestion_desc' | translate }}</span>
          </div>
          <p-checkbox [(ngModel)]="settings.newAppointmentSuggestion" [binary]="true"></p-checkbox>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-3 pt-2">
          <app-button [label]="'button_cancel' | translate" variant="ghost" (onClick)="onClose()"></app-button>
          <app-button [label]="'common_save' | translate" icon="pi pi-check" [loading]="loading" (onClick)="save()"></app-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class DtpProcedureSettingsModal implements OnInit {
  @Input() procedure: any;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  loading = false;
  settings = {
    isDefault: false,
    newAppointmentSuggestion: false
  };

  ngOnInit() {
    if (this.procedure) {
      this.settings.isDefault = !!this.procedure.isDefault;
      this.settings.newAppointmentSuggestion = !!this.procedure.newAppointmentSuggestion;
    }
  }

  onClose() {
    this.close.emit();
  }

  async save() {
    this.loading = true;
    try {
      await firstValueFrom(this.settingsService.updateProcedureSettings(this.procedure.id, this.settings));
      toast.success(this.translate.instant('dtp_settings_updated'));
      this.saved.emit();
    } catch (error) {
      console.error(error);
      toast.error(this.translate.instant('dtp_settings_update_failed'));
    } finally {
      this.loading = false;
    }
  }
}
